import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  MessageSquare,
  Users,
  Zap,
  Magnet,
  Settings,
  LogOut,
} from 'lucide-react';
import { navItems } from '../../data/mockData';
import { useAuth } from '../../context/AuthContext';
import { logout } from '../../firebase/auth.service';
import { getUserDisplayName, getUserInitials } from '../../utils/userName';

const iconMap = {
  'layout-dashboard': LayoutDashboard,
  'message-square': MessageSquare,
  'users': Users,
  'zap': Zap,
  'magnet': Magnet,
  'settings': Settings,
};

const Sidebar = () => {
  const navigate = useNavigate();
  const { user, normalizedUser, userProfileLoading } = useAuth();

  const displayName = normalizedUser?.fullName || getUserDisplayName(normalizedUser);
  const avatarURL = normalizedUser?.photoURL || normalizedUser?.avatar || '';
  const initials = getUserInitials(normalizedUser);

  const [photoError, setPhotoError] = useState(false);
  // Reset error state whenever the URL changes (e.g. after a new upload)
  useEffect(() => { setPhotoError(false); }, [avatarURL]);
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      console.log('Sidebar user:', normalizedUser);
      console.log('Photo URL:', avatarURL);
    }
  }, [normalizedUser, avatarURL]);

  const handleLogout = async () => {
    try {
      await logout();
      localStorage.removeItem('uid');
      navigate('/login');
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  return (
    <aside className="w-64 bg-notion-sidebar border-r border-notion-border flex flex-col justify-between flex-shrink-0 z-20">
      {/* Logo & Navigation */}
      <div className="p-3">
        {/* Logo */}
        <div className="flex items-center gap-3 px-3 py-4 mb-4">
          <div className="w-6 h-6 bg-stone-800 rounded flex items-center justify-center text-white text-xs font-bold">
            K
          </div>
          <span className="font-semibold text-sm tracking-tight text-notion-text">
            Kaeros
          </span>
        </div>

        {/* Navigation Links */}
        <nav className="space-y-0.5">
          {navItems.map((item) => {
            const Icon = iconMap[item.icon];
            return (
              <NavLink
                key={item.id}
                to={item.path}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded text-sm transition-colors group ${isActive
                    ? 'bg-notion-hover font-medium text-notion-text'
                    : 'text-stone-600 hover:bg-notion-hover'
                  }`
                }
              >
                <Icon className="w-4 h-4 text-notion-text-light group-hover:text-stone-600" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
      </div>

      {/* Bottom Section — Settings link + User Profile + Logout */}
      <div className="p-3 border-t border-notion-border">
        {/* Settings link */}
        <NavLink
          to="/settings"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded text-sm transition-colors group mb-3 ${isActive
              ? 'bg-notion-hover font-medium text-notion-text'
              : 'text-stone-600 hover:bg-notion-hover'
            }`
          }
        >
          <Settings className="w-4 h-4 text-notion-text-light group-hover:text-stone-600" />
          Settings
        </NavLink>

        {/* User Profile Block */}
        {user && (
          <>
            <div className="flex items-center gap-3 px-3 py-2">
              {userProfileLoading ? (
                /* Skeleton while profile loads */
                <div className="w-10 h-10 rounded-full bg-stone-200 animate-pulse flex-shrink-0" />
              ) : avatarURL && !photoError ? (
                <img
                  src={avatarURL}
                  alt={displayName}
                  className="w-10 h-10 rounded-full object-cover border border-notion-border flex-shrink-0"
                  onError={() => setPhotoError(true)}
                />
              ) : (
                <div
                  className="w-10 h-10 rounded-full bg-stone-200 text-stone-700 border border-notion-border flex-shrink-0 flex items-center justify-center text-xs font-semibold"
                  aria-label={displayName}
                  title={displayName}
                >
                  {initials}
                </div>
              )}

              <span className="text-sm font-medium text-notion-text truncate">
                {userProfileLoading ? (
                  <span className="inline-block w-24 h-3.5 bg-stone-200 rounded animate-pulse" />
                ) : (
                  displayName
                )}
              </span>
            </div>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 w-full px-3 py-2 mt-1 rounded text-sm text-red-600 hover:bg-red-50 transition-colors focus:outline-none focus:ring-2 focus:ring-red-300"
            >
              <LogOut className="w-4 h-4 flex-shrink-0" />
              Logout
            </button>
          </>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;

